import {
  ReactElement,
  createContext,
  useContext,
  useEffect,
  useMemo,
} from 'react';

import { Region } from '../../../utils';
import { useAuthFactory } from '../AuthFactoryContext';
import { UseAuthContextType } from '../auth0/auth.types';
import { getDefaultUseAuthState } from '../auth0/helpers';
import {
  getIdentityFromToken,
  getOrgNameFromToken,
  getRegionFromTokenOrDefault,
} from '../auth0/jwt';

type OverrideAuthContextType = UseAuthContextType;

const OverrideAuthDefaultState = getDefaultUseAuthState({
  user: { name: 'override', picture: undefined, email: 'email@email.com' },
  isAuthenticated: true,
});

const OverrideAuthContext = createContext<OverrideAuthContextType>(
  OverrideAuthDefaultState,
);

interface AuthenticationProviderProps {
  children: ReactElement;
  onTokenReceived: (token: string, region: Region) => void;
}

export const AuthenticationProvider = ({
  onTokenReceived,
  children,
}: AuthenticationProviderProps) => {
  const { overrideToken, setOverrideToken } = useAuthFactory();

  const value = useMemo<OverrideAuthContextType>(() => {
    const orgName = overrideToken
      ? getOrgNameFromToken(overrideToken)
      : undefined;
    const identity = overrideToken
      ? getIdentityFromToken(overrideToken)
      : undefined;

    return {
      ...OverrideAuthDefaultState,
      token: overrideToken,
      orgName,
      identity,
      getAccessTokenSilently: () => Promise.resolve(overrideToken ?? ''),
      logout: async () => setOverrideToken(undefined),
    };
  }, [overrideToken, setOverrideToken]);

  useEffect(() => {
    if (value.token) {
      onTokenReceived(value.token, getRegionFromTokenOrDefault(value.token));
    }
  }, [value, onTokenReceived]);

  return (
    <OverrideAuthContext.Provider value={value}>
      {children}
    </OverrideAuthContext.Provider>
  );
};

export const useAuth = () => useContext(OverrideAuthContext);
